import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  COPY_SUCCESS_MS,
  buildMailtoShareHref,
  buildSmsShareHref,
  buildWhatsAppShareHref,
  canUseNativeShare,
  copyTextToClipboard,
} from "./referralShare";

export function useReferralShare({
  code,
  shareUrl,
}: {
  code: string | undefined;
  shareUrl: string | undefined;
}) {
  const { t } = useTranslation();
  const [linkCopied, setLinkCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const linkCopiedTimeoutId = useRef<
    ReturnType<typeof window.setTimeout> | undefined
  >(undefined);
  const codeCopiedTimeoutId = useRef<
    ReturnType<typeof window.setTimeout> | undefined
  >(undefined);

  useEffect(() => {
    return () => {
      window.clearTimeout(linkCopiedTimeoutId.current);
      window.clearTimeout(codeCopiedTimeoutId.current);
    };
  }, []);

  const resolvedShareUrl = shareUrl ?? "";
  const shareMessage = t(
    "Get $5 off your Certn background check with my referral link: {{url}}",
    { url: resolvedShareUrl }
  );
  const emailSubject = t("Get $5 off your Certn background check");

  const markLinkCopied = () => {
    window.clearTimeout(linkCopiedTimeoutId.current);
    setLinkCopied(true);
    linkCopiedTimeoutId.current = window.setTimeout(
      () => setLinkCopied(false),
      COPY_SUCCESS_MS
    );
  };

  const copyShareUrl = () => {
    if (!resolvedShareUrl) {
      return;
    }
    copyTextToClipboard(resolvedShareUrl)
      .then(markLinkCopied)
      .catch((error) => {
        console.warn(error);
        setLinkCopied(false);
      });
  };

  const copyCode = () => {
    if (!code) {
      return;
    }
    copyTextToClipboard(code)
      .then(() => {
        window.clearTimeout(codeCopiedTimeoutId.current);
        setCodeCopied(true);
        codeCopiedTimeoutId.current = window.setTimeout(
          () => setCodeCopied(false),
          COPY_SUCCESS_MS
        );
      })
      .catch((error) => {
        console.warn(error);
        setCodeCopied(false);
      });
  };

  const shareNative = () => {
    if (!resolvedShareUrl) {
      return;
    }
    if (!canUseNativeShare()) {
      copyShareUrl();
      return;
    }

    navigator
      .share({
        title: t("Refer and earn"),
        text: shareMessage,
      })
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        copyShareUrl();
      });
  };

  return {
    shareUrl: resolvedShareUrl,
    shareMessage,
    emailSubject,
    whatsAppHref: buildWhatsAppShareHref(shareMessage),
    smsHref: buildSmsShareHref(shareMessage),
    mailtoHref: buildMailtoShareHref(emailSubject, shareMessage),
    copyShareUrl,
    copyCode,
    shareNative,
    linkCopied,
    codeCopied,
  };
}
